import { useEffect, useRef } from 'react';
import type { ColumnId } from '@planning-session/shared';
import type { SessionCredentials } from '../../lib/useSession.js';
import { useSession } from '../../lib/useSession.js';
import { Notice } from '../Notice.js';
import { RoomHeader } from '../RoomHeader.js';
import { Button } from '../ui/Button.js';
import { Board } from './Board.js';
import { PresenceAvatars } from './PresenceAvatars.js';
import { RetroAside } from './RetroAside.js';
import styles from './RetroRoom.module.css';

export interface RetroRoomProps {
  readonly credentials: SessionCredentials;
  onExit: () => void;
}

export function RetroRoom({ credentials, onExit }: RetroRoomProps) {
  const session = useSession(credentials);
  const room = session.room && session.room.kind === 'retro' ? session.room : null;
  const isHost = room?.you?.isHost ?? false;
  const isPlayer = room?.you?.role === 'player';

  // Read through a ref so the listener is attached once rather than swapped on
  // every render as `session` (fresh from useSession each render) changes identity.
  const latest = useRef({ isHost, phase: room?.phase, send: session.send });

  useEffect(() => {
    latest.current = { isHost, phase: room?.phase, send: session.send };
  });

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const target = event.target;
      const typing = target instanceof HTMLElement && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (typing || event.metaKey || event.ctrlKey || event.altKey) return;
      const current = latest.current;
      // Shift is not treated as a modifier, since `R` is how you type `r` with caps on.
      if (event.key.toLowerCase() === 'r' && current.isHost && current.phase === 'writing') {
        event.preventDefault();
        current.send({ type: 'reveal' });
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  function leave() {
    session.leave();
    onExit();
  }

  if (session.fatal) {
    return (
      <div className={styles.centered}>
        <div className={styles.stateCard}>
          <h1 className={styles.stateTitle}>Could not join</h1>
          <p className={styles.stateBody}>{session.fatal}</p>
          <Button variant="primary" onClick={onExit}>
            Back to the start
          </Button>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className={styles.centered}>
        <div className={styles.stateCard}>
          <h1 className={styles.stateTitle}>Pulling up a chair…</h1>
          <p className={styles.stateBody}>Connecting to retro {credentials.roomId}.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      <RoomHeader
        brand="Retro"
        title={room.title}
        joinPath="retro"
        roomId={room.id}
        you={room.you}
        status={session.status}
        presence={<PresenceAvatars participants={room.participants} />}
        onLeave={leave}
      />

      <div className={styles.main}>
        <section className={styles.stage}>
          <Board
            room={room}
            isPlayer={isPlayer}
            isHost={isHost}
            onAddNote={(column: ColumnId, text) => session.send({ type: 'addNote', column, text })}
            onEditNote={(noteId, text) => session.send({ type: 'editNote', noteId, text })}
            onRemoveNote={(noteId) => session.send({ type: 'removeNote', noteId })}
            onVoteNote={(noteId) => session.send({ type: 'voteNote', noteId })}
            onUnvoteNote={(noteId) => session.send({ type: 'unvoteNote', noteId })}
            onCombineNotes={(noteId, intoNoteId) => session.send({ type: 'combineNotes', noteId, intoNoteId })}
            onUnmergeNote={(noteId) => session.send({ type: 'unmergeNote', noteId })}
          />
        </section>

        <RetroAside
          room={room}
          isHost={isHost}
          isPlayer={isPlayer}
          onMode={(mode) => session.send({ type: 'setMode', mode })}
          onRole={(role) => session.send({ type: 'setRole', role })}
          onTransferHost={(participantId) => session.send({ type: 'transferHost', participantId })}
          onRemoveParticipant={(participantId) => session.send({ type: 'removeParticipant', participantId })}
          onReveal={() => session.send({ type: 'reveal' })}
          onUnreveal={() => session.send({ type: 'unreveal' })}
        />
      </div>

      {session.notice && <Notice message={session.notice} onDismiss={session.dismissNotice} />}
    </div>
  );
}
